import React, { useState, useCallback, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ZoomIn, ZoomOut, RotateCw, Check, X, Maximize2, Eraser, Loader2, FlipHorizontal2, RectangleHorizontal, Square, Crop } from 'lucide-react';

function createImage(url) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (e) => reject(e));
        image.crossOrigin = 'anonymous';
        image.src = url;
    });
}

function getRadianAngle(degreeValue) {
    return (degreeValue * Math.PI) / 180;
}

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0, maxHeight = null) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const rotRad = getRadianAngle(rotation);
    const { width: bBoxWidth, height: bBoxHeight } = (() => {
        const abs = Math.abs;
        return {
            width: abs(Math.cos(rotRad) * image.width) + abs(Math.sin(rotRad) * image.height),
            height: abs(Math.sin(rotRad) * image.width) + abs(Math.cos(rotRad) * image.height),
        };
    })();

    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0);

    let outW = pixelCrop.width;
    let outH = pixelCrop.height;
    if (maxHeight && outH > maxHeight) {
        const scale = maxHeight / outH;
        outH = Math.round(maxHeight);
        outW = Math.round(pixelCrop.width * scale);
    }

    const croppedCanvas = document.createElement('canvas');
    const croppedCtx = croppedCanvas.getContext('2d');
    croppedCanvas.width = outW;
    croppedCanvas.height = outH;

    croppedCtx.fillStyle = '#FFFFFF';
    croppedCtx.fillRect(0, 0, outW, outH);

    croppedCtx.drawImage(
        canvas,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, outW, outH
    );

    return new Promise((resolve) => {
        croppedCanvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
    });
}

async function getFreeCroppedImg(imageSrc, crop, imageEl, maxHeight = null) {
    const scaleX = imageEl.naturalWidth / imageEl.width;
    const scaleY = imageEl.naturalHeight / imageEl.height;
    const pixelX = crop.x * scaleX;
    const pixelY = crop.y * scaleY;
    const pixelW = crop.width * scaleX;
    const pixelH = crop.height * scaleY;

    const image = await createImage(imageSrc);
    let outW = Math.round(pixelW);
    let outH = Math.round(pixelH);
    if (maxHeight && outH > maxHeight) {
        const scale = maxHeight / outH;
        outH = Math.round(maxHeight);
        outW = Math.round(pixelW * scale);
    }

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(image, pixelX, pixelY, pixelW, pixelH, 0, 0, outW, outH);

    return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
    });
}

export default function ImageCropDialog({ open, onClose, imageSrc, onCropped, defaultMaxHeight, aspect: defaultAspect = 4 / 3 }) {
    // Shared state
    const [maxHeight, setMaxHeight] = useState('');
    const [originalSize, setOriginalSize] = useState(null);
    const [currentSrc, setCurrentSrc] = useState(null);
    const [removingBg, setRemovingBg] = useState(false);
    const [flipped, setFlipped] = useState(false);
    const [cropMode, setCropMode] = useState('ratio'); // 'ratio' or 'free'

    // react-easy-crop state (ratio mode)
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [activeAspect, setActiveAspect] = useState(defaultAspect);

    // react-image-crop state (free mode)
    const [freeCrop, setFreeCrop] = useState(undefined);
    const freeImgRef = useRef(null);

    useEffect(() => {
        if (imageSrc) {
            setCurrentSrc(imageSrc);
            const img = new Image();
            img.onload = () => setOriginalSize({ w: img.width, h: img.height });
            img.src = imageSrc;
            setMaxHeight(defaultMaxHeight ? String(defaultMaxHeight) : '');
            setActiveAspect(defaultAspect);
            setCropMode('ratio');
            setFreeCrop(undefined);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setRotation(0);
            setFlipped(false);
        }
    }, [imageSrc, defaultMaxHeight, defaultAspect]);

    const onCropComplete = useCallback((_, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const handleRemoveBg = async () => {
        if (!currentSrc) return;
        setRemovingBg(true);
        try {
            const response = await fetch(currentSrc);
            const blob = await response.blob();
            const formData = new FormData();
            formData.append('file', blob, 'image.png');
            const apiRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/remove-background`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                body: formData,
            });
            if (!apiRes.ok) throw new Error('Erreur API');
            const resultBlob = await apiRes.blob();
            const newUrl = URL.createObjectURL(resultBlob);
            setCurrentSrc(newUrl);
            const img = new Image();
            img.onload = () => setOriginalSize({ w: img.width, h: img.height });
            img.src = newUrl;
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setFreeCrop(undefined);
        } catch {
            alert('Erreur lors de la suppression du fond');
        } finally {
            setRemovingBg(false);
        }
    };

    const handleMirror = async () => {
        if (!currentSrc) return;
        const img = await createImage(currentSrc);
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
            const newUrl = URL.createObjectURL(blob);
            setCurrentSrc(newUrl);
            setCrop({ x: 0, y: 0 });
            setFreeCrop(undefined);
            setFlipped(f => !f);
        }, 'image/png');
    };

    const handleConfirm = async () => {
        const mh = maxHeight ? parseInt(maxHeight, 10) : null;
        if (cropMode === 'free') {
            if (!freeCrop || !freeImgRef.current || freeCrop.width < 1 || freeCrop.height < 1) return;
            const blob = await getFreeCroppedImg(currentSrc, freeCrop, freeImgRef.current, mh > 0 ? mh : null);
            const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
            const previewUrl = URL.createObjectURL(blob);
            onCropped(file, previewUrl, mh > 0 ? mh : null);
        } else {
            if (!croppedAreaPixels) return;
            const blob = await getCroppedImg(currentSrc, croppedAreaPixels, rotation, mh > 0 ? mh : null);
            const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
            const previewUrl = URL.createObjectURL(blob);
            onCropped(file, previewUrl, mh > 0 ? mh : null);
        }
        onClose();
    };

    const handleReset = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setMaxHeight('');
        setFlipped(false);
        setCurrentSrc(imageSrc);
        setFreeCrop(undefined);
    };

    const switchToFree = () => {
        setCropMode('free');
        setFreeCrop(undefined);
    };

    const switchToRatio = (ratio) => {
        setCropMode('ratio');
        setActiveAspect(ratio);
    };

    // Compute output dimensions for display
    let cropW = 0, cropH = 0;
    if (cropMode === 'free' && freeCrop && freeImgRef.current) {
        const scaleX = (originalSize?.w || 1) / (freeImgRef.current?.width || 1);
        const scaleY = (originalSize?.h || 1) / (freeImgRef.current?.height || 1);
        cropW = Math.round((freeCrop.width || 0) * scaleX);
        cropH = Math.round((freeCrop.height || 0) * scaleY);
    } else {
        cropW = croppedAreaPixels?.width || 0;
        cropH = croppedAreaPixels?.height || 0;
    }
    const mh = maxHeight ? parseInt(maxHeight, 10) : 0;
    const finalH = (mh > 0 && cropH > mh) ? mh : cropH;
    const finalW = (mh > 0 && cropH > mh) ? Math.round(cropW * mh / cropH) : cropW;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                className="max-w-lg p-0 overflow-hidden flex flex-col h-[100dvh] sm:h-auto sm:max-h-[92dvh]"
                data-testid="image-crop-dialog"
            >
                <DialogHeader className="px-4 pt-4 pb-2 flex-shrink-0">
                    <DialogTitle>Recadrer la photo</DialogTitle>
                    <DialogDescription>Ajustez le cadrage avec le zoom et le deplacement.</DialogDescription>
                </DialogHeader>

                <div className="flex-1 min-h-0 overflow-y-auto">
                {/* Crop area */}
                <div className="relative w-full h-[40dvh] sm:h-[350px] bg-slate-900 overflow-hidden">
                    {removingBg && (
                        <div className="absolute inset-0 z-10 bg-black/60 flex flex-col items-center justify-center gap-2">
                            <Loader2 className="w-8 h-8 animate-spin text-white" />
                            <span className="text-white text-sm">Suppression du fond...</span>
                        </div>
                    )}
                    {cropMode === 'ratio' ? (
                        <Cropper
                            image={currentSrc}
                            crop={crop}
                            zoom={zoom}
                            minZoom={0.3}
                            rotation={rotation}
                            aspect={activeAspect}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                            restrictPosition={false}
                            objectFit="contain"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center overflow-auto p-2">
                            <ReactCrop
                                crop={freeCrop}
                                onChange={(c) => setFreeCrop(c)}
                                className="max-h-full"
                            >
                                <img
                                    ref={freeImgRef}
                                    src={currentSrc}
                                    alt="crop"
                                    style={{ maxHeight: '334px', maxWidth: '100%', display: 'block' }}
                                    crossOrigin="anonymous"
                                />
                            </ReactCrop>
                        </div>
                    )}
                </div>

                {/* Controls */}
                <div className="px-4 py-3 space-y-3">
                    {/* Zoom slider - only for ratio mode */}
                    {cropMode === 'ratio' && (
                        <div className="flex items-center gap-3">
                            <ZoomOut className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <input
                                type="range"
                                min={0.3}
                                max={3}
                                step={0.05}
                                value={zoom}
                                onChange={(e) => setZoom(Number(e.target.value))}
                                className="flex-1 accent-red-600 h-1.5"
                                data-testid="crop-zoom-slider"
                            />
                            <ZoomIn className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        </div>
                    )}

                    {cropMode === 'free' && (
                        <p className="text-xs text-slate-500 italic px-1">
                            Dessinez un rectangle sur l'image pour selectionner la zone a rogner.
                        </p>
                    )}

                    {/* Resize control */}
                    <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2">
                        <Maximize2 className="w-4 h-4 text-slate-500 flex-shrink-0" />
                        <div className="flex items-center gap-2 flex-1">
                            <label className="text-xs text-slate-500 whitespace-nowrap">Hauteur max :</label>
                            <input
                                type="number"
                                placeholder={cropH > 0 ? String(cropH) : '---'}
                                value={maxHeight}
                                onChange={(e) => setMaxHeight(e.target.value)}
                                className="w-20 text-sm border border-slate-200 rounded px-2 py-0.5 text-center"
                                min={50}
                                data-testid="crop-max-height-input"
                            />
                            <span className="text-xs text-slate-400">px</span>
                        </div>
                        <span className="text-xs text-slate-500 whitespace-nowrap" data-testid="crop-output-size">
                            {finalW} x {finalH}
                        </span>
                    </div>
                    {originalSize && (
                        <p className="text-[10px] text-slate-400 px-1">
                            Image source : {originalSize.w} x {originalSize.h} px
                            {cropW > 0 && ` | Selection : ${cropW} x ${cropH} px`}
                        </p>
                    )}

                    {/* Mode selector: Ratio presets + Free */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-500">Ratio :</span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => switchToRatio(1)}
                            className={`text-xs px-2.5 ${cropMode === 'ratio' && activeAspect === 1 ? 'border-red-400 text-red-600 bg-red-50' : ''}`}
                            data-testid="aspect-1-1-btn"
                        >
                            <Square className="w-3.5 h-3.5 mr-1" /> 1:1
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => switchToRatio(4 / 3)}
                            className={`text-xs px-2.5 ${cropMode === 'ratio' && Math.abs(activeAspect - 4/3) < 0.01 ? 'border-red-400 text-red-600 bg-red-50' : ''}`}
                            data-testid="aspect-4-3-btn"
                        >
                            <RectangleHorizontal className="w-3.5 h-3.5 mr-1" /> 4:3
                        </Button>
                        {defaultAspect !== 1 && Math.abs(defaultAspect - 4/3) > 0.01 && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => switchToRatio(defaultAspect)}
                                className={`text-xs px-2.5 ${cropMode === 'ratio' && Math.abs(activeAspect - defaultAspect) < 0.01 ? 'border-red-400 text-red-600 bg-red-50' : ''}`}
                                data-testid="aspect-default-btn"
                            >
                                <RectangleHorizontal className="w-3.5 h-3.5 mr-1" /> {Math.round(defaultAspect * 9)}/{9}
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={switchToFree}
                            className={`text-xs px-2.5 ${cropMode === 'free' ? 'border-red-400 text-red-600 bg-red-50' : ''}`}
                            data-testid="aspect-free-btn"
                        >
                            <Crop className="w-3.5 h-3.5 mr-1" /> Libre
                        </Button>
                    </div>

                    {/* Rotation + Mirror + Remove BG + Reset */}
                    <div className="flex items-center gap-2 flex-wrap">
                        {cropMode === 'ratio' && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setRotation(r => (r + 90) % 360)}
                                data-testid="crop-rotate-btn"
                            >
                                <RotateCw className="w-4 h-4 mr-1" /> Rotation
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleMirror}
                            data-testid="crop-mirror-btn"
                            className={flipped ? 'border-blue-400 text-blue-600 bg-blue-50' : ''}
                        >
                            <FlipHorizontal2 className="w-4 h-4 mr-1" /> Miroir
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRemoveBg}
                            disabled={removingBg}
                            data-testid="crop-remove-bg-btn"
                            className="text-violet-600 border-violet-200 hover:bg-violet-50"
                        >
                            {removingBg ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eraser className="w-4 h-4 mr-1" />}
                            Suppr. fond
                        </Button>
                        <Button variant="ghost" size="sm" onClick={handleReset} className="text-slate-500">
                            Reinitialiser
                        </Button>
                    </div>
                </div>

                </div>

                <DialogFooter className="px-4 pb-4 pt-3 flex-shrink-0 border-t bg-white">
                    <Button variant="outline" onClick={onClose}>
                        <X className="w-4 h-4 mr-1" /> Annuler
                    </Button>
                    <Button onClick={handleConfirm} className="bg-red-600 hover:bg-red-700 text-white" data-testid="crop-confirm-btn">
                        <Check className="w-4 h-4 mr-1" /> Valider
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
